import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MessageSquareQuote, Plus, Trash2, Sparkles } from 'lucide-react';
import { fetchCannedMessages, saveCannedMessages } from '../../../api/ivrAgents.js';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * CannedMessagesModal — 罐頭訊息（文字聊天建議問題）
 *
 *   開關（預設關）+ 開場問題清單編輯。開啟後 share-web 文字聊天：
 *     - 對話開頭顯示這些問題 chips 供點選
 *     - 每輪 AI 回覆後自動生成「建議追問」chips（backend 生成，毋須設定）
 */
const MAX_MESSAGES = 8;

export function CannedMessagesModal({ open, onClose, avatarId }) {
    const { t } = useTranslation('wsModals');
    const [enabled, setEnabled] = useState(false);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setError(''); setSaving(false); setLoading(true);
        fetchCannedMessages(avatarId)
            .then((r) => {
                if (cancelled) return;
                setEnabled(r?.enabled === true);
                setMessages(Array.isArray(r?.messages) && r.messages.length ? r.messages : ['']);
            })
            .catch((e) => { if (!cancelled) setError(e.message || 'load failed'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, avatarId]);

    if (!open) return null;

    const upd = (i, v) => setMessages((a) => a.map((m, idx) => (idx === i ? v : m)));
    const add = () => setMessages((a) => (a.length >= MAX_MESSAGES ? a : [...a, '']));
    const rm = (i) => setMessages((a) => a.filter((_, idx) => idx !== i));

    const handleSave = async () => {
        if (saving) return;
        const cleaned = messages.map((m) => m.trim()).filter(Boolean);
        if (enabled && cleaned.length === 0) { setError(t('canned.needOne')); return; }
        setError(''); setSaving(true);
        try {
            await saveCannedMessages(avatarId, { enabled, messages: cleaned });
            onClose();
        } catch (e) {
            setError(e.message || 'save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2.5">
                        <div className="inline-flex items-center justify-center size-8 rounded-md bg-accent-50">
                            <MessageSquareQuote size={17} strokeWidth={1.6} className="text-accent-700" />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-ink-900">{t('canned.title')}</div>
                            <div className="text-[11px] text-ink-400">{t('canned.subtitle')}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-cream-100 text-ink-400 hover:text-ink-700 transition">
                        <X size={16} />
                    </button>
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {/* enable toggle */}
                    <label className="flex items-start justify-between gap-3 cursor-pointer select-none">
                        <div>
                            <div className="text-sm text-ink-900">{t('canned.enableLabel')}</div>
                            <div className="text-[11px] text-ink-400 mt-0.5 leading-snug">{t('canned.enableHint')}</div>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={enabled}
                            onClick={() => setEnabled((v) => !v)}
                            className={[
                                'relative shrink-0 w-10 h-6 rounded-full transition-colors mt-0.5',
                                enabled ? 'bg-accent-500' : 'bg-cream-300',
                            ].join(' ')}
                        >
                            <span className={[
                                'absolute left-0 top-0.5 size-5 rounded-full bg-white shadow transition-transform',
                                enabled ? 'translate-x-[18px]' : 'translate-x-0.5',
                            ].join(' ')} />
                        </button>
                    </label>

                    {/* starter question list */}
                    <div className={enabled ? '' : 'opacity-45 pointer-events-none'}>
                        <div className="text-[12px] font-medium text-ink-700 mb-2">{t('canned.listLabel')}</div>
                        <div className="space-y-2">
                            {messages.map((m, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input
                                        value={m}
                                        onChange={(e) => upd(i, e.target.value)}
                                        maxLength={120}
                                        placeholder={t('canned.itemPlaceholder')}
                                        className="flex-1 rounded-md border border-cream-300 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-500/15"
                                    />
                                    <button onClick={() => rm(i)} className="p-2 rounded-md text-ink-300 hover:text-danger-500 hover:bg-cream-100 transition" title={t('canned.remove')}>
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {messages.length < MAX_MESSAGES && (
                            <button onClick={add} className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-accent-700 hover:text-accent-600 transition">
                                <Plus size={14} /> {t('canned.add')}
                            </button>
                        )}

                        {/* followup note */}
                        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5">
                            <Sparkles size={14} className="text-accent-600 shrink-0 mt-0.5" />
                            <div className="text-[11px] text-ink-500 leading-snug">{t('canned.followupNote')}</div>
                        </div>
                    </div>

                    {error && <div className="text-[12px] text-danger-500">{error}</div>}
                </div>

                {/* footer */}
                <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-cream-200">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-md text-ink-500 hover:bg-cream-100 transition">{t('canned.cancel')}</button>
                    <button onClick={handleSave} disabled={saving || loading} className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed">
                        {saving ? t('canned.saving') : t('canned.saveBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}
